import { useState, useEffect } from 'react';
import { Building2, Trash2, RotateCcw, Save, Upload, Image, Phone, Mail, Globe, FileText, Eye, MapPin } from 'lucide-react';

const CompanySettings = ({ settings, onSave, onChange }) => {
  const [formData, setFormData] = useState({
    name: settings?.name || '',
    address: settings?.address || '',
    phone: settings?.phone || '',
    email: settings?.email || '',
    website: settings?.website || '',
    companyNumber: settings?.companyNumber || '',
    vatNumber: settings?.vatNumber || '',
    logo: settings?.logo || null
  });

  const [logoPreview, setLogoPreview] = useState(settings?.logo || null);
  const [activeTab, setActiveTab] = useState('basic');
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (onChange) {
      onChange();
    }
  }, [formData, onChange]);

  const handleSubmit = () => {
    onSave({ ...settings, ...formData });
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (2MB limit)
      if (file.size > 2 * 1024 * 1024) {
        alert('File size must be less than 2MB');
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const logoData = event.target.result;
        setLogoPreview(logoData);
        setFormData({ ...formData, logo: logoData });
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setLogoPreview(null);
    setFormData({ ...formData, logo: null });
  };

  const resetForm = () => {
    setFormData({
      name: settings?.name || '',
      address: settings?.address || '',
      phone: settings?.phone || '',
      email: settings?.email || '',
      website: settings?.website || '',
      companyNumber: settings?.companyNumber || '',
      vatNumber: settings?.vatNumber || '',
      logo: settings?.logo || null
    });
    setLogoPreview(settings?.logo || null);
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: Building2 },
    { id: 'contact', label: 'Contact Details', icon: Phone },
    { id: 'legal', label: 'Legal Details', icon: FileText },
    { id: 'branding', label: 'Logo & Branding', icon: Image }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Company Identity</h3>
              <p className="text-blue-700 text-sm mb-6">Basic information that identifies your business</p>
              
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Your Company Name Ltd"
                    />
                    <p className="text-xs text-gray-500 mt-1">This appears as the main heading on all your documents</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <MapPin size={16} className="inline mr-1" />
                      Business Address
                    </label>
                    <textarea
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                      rows="4"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="123 Business Street&#10;Business Park&#10;London, England&#10;SW1A 1AA"
                    />
                    <p className="text-xs text-gray-500 mt-1">Full registered business address including postcode</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'contact':
        return (
          <div className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-green-900 mb-2">Contact Information</h3>
              <p className="text-green-700 text-sm mb-6">How customers can reach you</p>
              
              <div className="space-y-6">
                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <h4 className="font-semibold text-gray-800 mb-4">Primary Contact</h4>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Mail size={16} className="inline mr-1" />
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="info@yourcompany.com"
                      />
                      <p className="text-xs text-gray-500 mt-1">Main business email address</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Phone size={16} className="inline mr-1" />
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+44 20 1234 5678"
                      />
                      <p className="text-xs text-gray-500 mt-1">Include country code for international customers</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <h4 className="font-semibold text-gray-800 mb-4">Online Presence</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Globe size={16} className="inline mr-1" />
                      Website URL
                    </label>
                    <input
                      type="url"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      placeholder="https://www.yourcompany.com"
                    />
                    <p className="text-xs text-gray-500 mt-1">Your company website (include https://)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'legal':
        return (
          <div className="space-y-6">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-purple-900 mb-2">Legal & Registration Details</h3>
              <p className="text-purple-700 text-sm mb-6">Official registration numbers and legal information</p>
              
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Company Registration Number</label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none font-mono"
                      value={formData.companyNumber}
                      onChange={(e) => setFormData({ ...formData, companyNumber: e.target.value })}
                      placeholder="12345678"
                    />
                    <p className="text-xs text-gray-500 mt-1">Companies House registration number (UK)</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">VAT Registration Number</label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none font-mono"
                      value={formData.vatNumber}
                      onChange={(e) => setFormData({ ...formData, vatNumber: e.target.value.toUpperCase() })}
                      placeholder="GB123456789"
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave blank if not VAT registered</p>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <span className="font-medium">Note:</span> These registration numbers will appear on your invoices as required by law. Ensure they are accurate and up to date.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'branding':
        return (
          <div className="space-y-6">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-orange-900 mb-2">Logo & Branding</h3>
              <p className="text-orange-700 text-sm mb-6">Upload your company logo for professional documents</p>
              
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                {logoPreview ? (
                  <div className="text-center">
                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-800 mb-3">Current Logo</h4>
                      <div className="inline-block p-6 bg-gray-50 border border-gray-200 rounded-lg">
                        <img 
                          src={logoPreview} 
                          alt="Company logo preview" 
                          className="max-h-32 max-w-48 mx-auto"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Replace Logo</label>
                        <input
                          type="file"
                          className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 file:cursor-pointer cursor-pointer"
                          accept="image/*"
                          onChange={handleLogoUpload}
                        />
                      </div>
                      
                      <div className="flex items-end">
                        <button 
                          type="button" 
                          className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                          onClick={removeLogo}
                        >
                          <Trash2 size={16} className="mr-2" />
                          Remove Logo
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="mb-6">
                      <div className="inline-flex items-center justify-center w-24 h-24 bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg">
                        <Upload size={32} className="text-gray-400" />
                      </div>
                    </div>
                    
                    <h4 className="font-semibold text-gray-800 mb-2">Upload Company Logo</h4>
                    <p className="text-sm text-gray-600 mb-4">Add your logo to appear on all invoices and quotes</p>
                    
                    <div className="max-w-sm mx-auto">
                      <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <Upload size={24} className="text-gray-400 mb-2" />
                          <p className="text-sm text-gray-500">
                            <span className="font-semibold">Click to upload</span> or drag and drop
                          </p>
                          <p className="text-xs text-gray-500">PNG, JPG, SVG up to 2MB</p>
                        </div>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={handleLogoUpload}
                        />
                      </label>
                    </div>
                  </div>
                )}

                <div className="mt-6 space-y-3">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h5 className="font-medium text-blue-800 mb-2">Logo Guidelines</h5>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• Use PNG format with transparent background for best results</li>
                      <li>• Recommended size: 300px wide × 100px tall maximum</li>
                      <li>• Keep file size under 2MB for faster loading</li>
                      <li>• Avoid very detailed logos as they may not print clearly</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const getCompletionStatus = () => {
    const requiredFields = ['name', 'email'];
    const optionalFields = ['address', 'phone', 'website', 'companyNumber', 'vatNumber', 'logo'];
    
    const requiredComplete = requiredFields.filter(field => formData[field]).length;
    const optionalComplete = optionalFields.filter(field => formData[field]).length;
    const totalFields = requiredFields.length + optionalFields.length;
    const completedFields = requiredComplete + optionalComplete;
    
    return {
      percentage: Math.round((completedFields / totalFields) * 100),
      requiredComplete: requiredComplete === requiredFields.length,
      completedFields,
      totalFields
    };
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-start">
          <div>
            <h5 className="flex items-center text-xl font-semibold text-gray-800">
              <Building2 size={24} className="mr-3" />
              Company Information
            </h5>
            <p className="text-sm text-gray-600 mt-1">
              This information appears on your invoices, quotes, and other business documents
            </p>
          </div>
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Eye size={16} className="mr-2" />
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </button>
        </div>
      </div>

      <div className="p-6">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-1 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon size={16} className="mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        {renderTabContent()}

        {/* Preview Section */}
        {showPreview && (
          <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-6">
            <h6 className="text-lg font-semibold text-gray-800 mb-4">Document Preview</h6>
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-start justify-between mb-6">
                <div className="flex-1">
                  {formData.name ? (
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">{formData.name}</h2>
                  ) : (
                    <div className="h-8 bg-gray-200 rounded w-64 mb-2"></div>
                  )}
                  
                  {formData.address ? (
                    <div className="text-sm text-gray-600 whitespace-pre-line mb-2">{formData.address}</div>
                  ) : (
                    <div className="space-y-1 mb-2">
                      <div className="h-4 bg-gray-200 rounded w-48"></div>
                      <div className="h-4 bg-gray-200 rounded w-40"></div>
                      <div className="h-4 bg-gray-200 rounded w-36"></div>
                    </div>
                  )}
                  
                  <div className="text-sm text-gray-600 space-y-1">
                    {formData.email && (
                      <div className="flex items-center">
                        <Mail size={14} className="mr-2" />
                        {formData.email}
                      </div>
                    )}
                    {formData.phone && (
                      <div className="flex items-center">
                        <Phone size={14} className="mr-2" />
                        {formData.phone}
                      </div>
                    )}
                    {formData.website && (
                      <div className="flex items-center">
                        <Globe size={14} className="mr-2" />
                        {formData.website}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="ml-8">
                  {logoPreview ? (
                    <img src={logoPreview} alt="Company logo" className="max-h-20 max-w-32" />
                  ) : (
                    <div className="w-32 h-20 bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                      <span className="text-xs text-gray-500">Logo</span>
                    </div>
                  )}
                </div>
              </div>
              
              {(formData.companyNumber || formData.vatNumber) && (
                <div className="pt-4 border-t border-gray-200 text-xs text-gray-500">
                  {formData.companyNumber && <div>Company Registration: {formData.companyNumber}</div>}
                  {formData.vatNumber && <div>VAT Registration: {formData.vatNumber}</div>}
                </div>
              )}
            </div>
            
            {/* Completion Status */}
            <div className="mt-4 p-4 bg-white border border-gray-200 rounded-lg">
              <div className="flex justify-between items-center">
                <div>
                  <h4 className="font-medium text-gray-800">Profile Completion</h4>
                  <p className="text-sm text-gray-600">
                    {getCompletionStatus().completedFields} of {getCompletionStatus().totalFields} fields completed
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-blue-600">{getCompletionStatus().percentage}%</div>
                  {getCompletionStatus().requiredComplete ? (
                    <div className="text-xs text-green-600">✓ Required fields complete</div>
                  ) : (
                    <div className="text-xs text-red-600">Required fields missing</div>
                  )}
                </div>
              </div>
              <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                  style={{ width: `${getCompletionStatus().percentage}%` }}
                ></div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between pt-8 border-t border-gray-200 mt-8">
          <button 
            type="button" 
            className="flex items-center px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            onClick={resetForm}
          >
            <RotateCcw size={16} className="mr-2" />
            Reset Changes
          </button>
          <button 
            type="button"
            onClick={handleSubmit}
            className="flex items-center px-6 py-3 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Save size={16} className="mr-2" />
            Save Company Information
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompanySettings;